import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import {
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  Line,
  Legend,
  Cell,
} from 'recharts';
function ShibaPage() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [shibaList, setShibaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [raceList, setRaceList] = useState([]);

  useEffect(() => {
    const fetchRaceList = async () => {
      try {
        // const res = await axios.get("http://127.0.0.1:8000/api/shiba");
        const res = await axios.get('https://chiriri-keiba.com/api/front_button_race_list');
        setRaceList(res.data.race_list);
      } catch (error) {
        console.error('データ取得失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRaceList();
  }, []);

  const fetchRaceData = async (race_info) => {
    try {
      // const res = await axios.get("http://127.0.0.1:8000/api/shiba");
      const res = await axios.get(`https://chiriri-keiba.com/api/race?race_info=${race_info}`);
      setShibaList(res.data);
    } catch (error) {
      console.error('データ取得失敗:', error);
    }
  };

  if (loading) {
    return <div>読み込み中...</div>;
  }

  // グラフ用資材
  // ％表示
  const pct = (v) => `${v}%`;

  // / 表示用データ構築（umaban, probability, tansho_odds, prediction）
  const buildChartData = (list) =>
    (list?.tanpuku_df ?? [])
      .map((r) => ({
        umaban: r.umaban,
        probability: Number(r.probability), // 0–100 の数値
        odds: Number(r.tansho_odds), // オッズ
        prediction: r.prediction, // ◎/○/▲/× など
      }))
      .sort((a, b) => b.probability - a.probability);

  // ◎だけ色を変える（好みで色は調整して）
  const barColor = (pred) => (pred === '◎' ? '#ff7043' : '#8884d8'); // ◎=オレンジ, それ以外=薄紫

  return (
    <Box p={{ xs: 0, sm: 4 }} sx={{ width: '100%' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontSize: { xs: '1.5rem', sm: '2.125rem' }, // スマホは少し小さく
        }}
      >
        {raceList.map((race, index) => (
          <Button variant="contained" color="primary" onClick={() => fetchRaceData(race)}>
            {race}
          </Button>
        ))}
      </Typography>

      {/* ボタン群の下あたりにグラフ追加 */}
      {shibaList?.tanpuku_df && shibaList.tanpuku_df.length > 0 && (
        <Box
          sx={{
            width: { xs: '100vw', sm: '100%' },
            ml: { xs: 'calc(50% - 50vw)', sm: 0 },
            mr: { xs: 'calc(50% - 50vw)', sm: 0 },
            height: { xs: 420, sm: 380 },
            mb: { xs: 8, sm: 6 },
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            単勝確率（%）＋ オッズ
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: 'gray' }}>
            ■棒グラフは単勝確率 ※オレンジ色は期待値以上の馬
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: 'gray' }}>
            ■折れ線グラフはオッズ
          </Typography>

          <ResponsiveContainer width="100%" height={isXs ? 440 : 380}>
            <BarChart
              data={buildChartData(shibaList)}
              margin={{ top: 10, right: 24, left: 8, bottom: isXs ? 60 : 24 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="umaban"
                interval={0}
                tick={{ fontSize: isXs ? 10 : 18 }}
                height={isXs ? 44 : 30}
                angle={isXs ? -35 : 0}
                textAnchor={isXs ? 'end' : 'middle'}
              />

              {/* 左：確率(%) */}
              <YAxis
                yAxisId="prob"
                domain={[0, 100]}
                tickFormatter={pct}
                tick={{ fontSize: isXs ? 10 : 18 }}
              />

              {/* 右：オッズ */}
              <YAxis
                yAxisId="odds"
                orientation="right"
                tickFormatter={(v) => v.toFixed(1)}
                allowDecimals
                tick={{ fontSize: isXs ? 10 : 18 }}
              />

              <Tooltip
                wrapperStyle={{ fontSize: isXs ? '0.7rem' : '0.85rem' }}
                formatter={(value, name) => {
                  if (name === 'probability') return [`${value}%`, '単勝確率'];
                  if (name === 'odds') return [value, '単勝オッズ'];
                  return [value, name];
                }}
                labelFormatter={(label, payload) => {
                  const p = payload?.[0]?.payload;
                  return `馬番 ${label}${p?.prediction ? `（${p.prediction}）` : ''}`;
                }}
              />

              <Legend wrapperStyle={{ fontSize: isXs ? '0.7rem' : '0.85rem' }} />

              {/* 棒：確率 */}
              <Bar yAxisId="prob" dataKey="probability" name="単勝確率">
                <LabelList
                  dataKey="probability"
                  position="top"
                  formatter={(v) => `${v}%`}
                  fontSize={isXs ? 8 : 24}
                />
                {buildChartData(shibaList).map((d, i) => (
                  <Cell key={`cell-${i}`} fill={barColor(d.prediction)} />
                ))}
              </Bar>

              {/* 折れ線：オッズ（右軸） */}
              <Line
                yAxisId="odds"
                type="monotone"
                dataKey="odds"
                name="単勝オッズ"
                dot
                activeDot
                strokeWidth={2}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ mt: { xs: 0, sm: 20 } }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                オッズ
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                単勝確率
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                新単勝確率
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                2着確率
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                3着確率
              </TableCell>
              {/* <TableCell
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" }, // スマホは少し小さく
                  padding: "3px",
                  textAlign: "center"
                }}
              >複勝確率</TableCell> */}
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                期待値
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                対象
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shibaList.tanpuku_df?.map((tanpuku) => (
              <TableRow key={tanpuku.id}>
                <TableCell>{tanpuku.umaban}</TableCell>
                <TableCell>{tanpuku.tansho_odds}</TableCell>
                <TableCell>{tanpuku.probability}%</TableCell>
                <TableCell>{tanpuku.newtanmodelprobability}%</TableCell>
                <TableCell>{tanpuku.nichakuprobabilitty}%</TableCell>
                <TableCell>{tanpuku.sanchakuprobabilitty}%</TableCell>
                {/* <TableCell>{tanpuku.fukushoprobabilitty}%</TableCell> */}
                <TableCell>{tanpuku.hit_predict_persent}</TableCell>
                <TableCell>{tanpuku.prediction}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                ワイド馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                ワイド馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                ワイド確率
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shibaList.wide_df?.map((wide) => (
              <TableRow key={wide.id}>
                <TableCell>{wide.umaban_a}</TableCell>
                <TableCell>{wide.umaban_b}</TableCell>
                <TableCell>{wide.prob}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                馬連馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                馬連馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                馬連確率
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shibaList.umaren_df?.map((umaren) => (
              <TableRow key={umaren.id}>
                <TableCell>{umaren.umaban_a}</TableCell>
                <TableCell>{umaren.umaban_b}</TableCell>
                <TableCell>{umaren.prob}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連複馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連複馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連複馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連複確率
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shibaList.sanrenpuku_df?.map((sanrenpuku) => (
              <TableRow key={sanrenpuku.id}>
                <TableCell>{sanrenpuku.umaban_a}</TableCell>
                <TableCell>{sanrenpuku.umaban_b}</TableCell>
                <TableCell>{sanrenpuku.umaban_c}</TableCell>
                <TableCell>{sanrenpuku.prob}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連単馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連単馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連単馬番
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }, // スマホは少し小さく
                  padding: '3px',
                  textAlign: 'center',
                }}
              >
                三連単確率
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shibaList.sanrentan_df?.map((sanrentan) => (
              <TableRow key={sanrentan.id}>
                <TableCell>{sanrentan.umaban_a}</TableCell>
                <TableCell>{sanrentan.umaban_b}</TableCell>
                <TableCell>{sanrentan.umaban_c}</TableCell>
                <TableCell>{sanrentan.prob}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ShibaPage;
